import { GridItem } from '@rsa-digital/evo-shared-components/components/Grid';
import RichText from '@rsa-digital/evo-shared-components/components/RichText';
import { graphql } from 'gatsby';
import React, { useState } from 'react';
import { processConfirmationModalData } from 'helpers/csTypeProcessors';
import { trackButtonClick } from 'helpers/eventTracking';
import {
  CsConfirmationModal,
  CsQuestion,
  CsQuestionWithOptions,
} from 'types/contentStack';
import ConfirmationModal from './ConfirmationModal';
import ProcessedFormField from './ProcessedFormField';
import { submitContactApi } from './api';
import { BodyTextGrid, StyledForm, StyledIcon, StyledSubmitButton } from './styles';
import { FieldValueType, FormDataType } from './types';
import useFormDataValidation from './useFormDataValidation';

export type EmailFormProps = {
  body?: string | null;
  submit_button_text: string;
  destination: string;
  confirmation_modal: CsConfirmationModal;
  fields: (CsQuestion | CsQuestionWithOptions)[];
};

const EmailForm: React.FC<EmailFormProps> = ({
  body,
  submit_button_text,
  fields,
  destination,
  confirmation_modal,
}) => {
  const confirmationModalData = processConfirmationModalData(confirmation_modal);

  const [formData, setFormData] = useState<FormDataType>({});
  const [isFieldError, setIsFieldError] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<Error | undefined>();
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [isConfirmationModalOn, setConfirmationModalOn] = useState<boolean>(false);

  const handleApiCall = async (): Promise<void> => {
    try {
      setSubmitError(undefined);
      setSubmitting(true);
      await submitContactApi(formData, destination, () => {
        setFormData({});
        setConfirmationModalOn(true);
      });
      setSubmitting(false);
    } catch (err) {
      setSubmitting(false);
      setSubmitError(err as Error);
    }
  };

  const { getError, validateFormData, resetFieldError } = useFormDataValidation(
    fields,
    formData
  );

  const updateData = (fieldKey: string, inputValue: FieldValueType): void => {
    resetFieldError(fieldKey);
    setIsFieldError(false);
    setFormData((prev) => ({ ...prev, [`${fieldKey}`]: inputValue }));
  };

  // Using any instead of FormEventHandler<HTMLFormElement> for access to preventDefault to avoid redirecting
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (e: any): void => {
    e.preventDefault();
    validateFormData(handleApiCall, () => {
      setIsFieldError(true);
    });
  };

  /* istanbul ignore if */
  if (submitError) {
    console.error(submitError.message);
  }

  return (
    <>
      {body && body !== null && (
        <BodyTextGrid data-cy="FormBodyText" alignLeft>
          <GridItem>
            <RichText html={body} />
          </GridItem>
        </BodyTextGrid>
      )}
      <StyledForm onSubmit={handleSubmit} data-cy="EmailForm">
        {fields.map((field: CsQuestion | CsQuestionWithOptions) => (
          <ProcessedFormField
            data-cy="FormField"
            key={`field-${field.value}`}
            answerData={formData}
            fieldData={field}
            onChange={updateData}
            errorMessage={getError(field.value)}
          />
        ))}
        <StyledSubmitButton
          data-cy="FormSubmitButton"
          type="submit"
          onClick={() => trackButtonClick('Email Form', submit_button_text)}
          disabled={isFieldError || isSubmitting}>
          {submit_button_text}
          <StyledIcon name="chevron" size="small" />
        </StyledSubmitButton>
      </StyledForm>
      {confirmationModalData && isConfirmationModalOn && (
        <ConfirmationModal
          {...confirmationModalData}
          onClose={() => setConfirmationModalOn(false)}
        />
      )}
    </>
  );
};

export const query = graphql`
  fragment EmailFormTemplate on cs__email_form_template {
    form_structure {
      body
      submit_button_text
      confirmation_modal {
        modal_title
        modal_body
      }
      fields {
        value
        type
        show_if_field {
          value
          to_contain
        }
        main_details {
          question_text
          explanatory_text
          alert_text
          tooltip {
            tooltip_text
            tooltip_open
            tooltip_close
          }
        }
        answer_options {
          name
          value
        }
        error_messages {
          missing
          invalid_value
        }
        additional_validators {
          date {
            future_invalid
            past_invalid
          }
        }
      }
    }
  }
`;

export default EmailForm;
